import * as yup from "yup";
import {useFormik} from "formik";
import {Button, Label, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import LabelInput from "../../../components/Common/LabelInput";
import Select from "../../../components/Common/Select";
import React, {useEffect, useState} from "react";
import {IErrorClassifierGroup, IErrorClassifierKeywordForm} from "../../../store/errorClassifier/types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ErrorClassifierService} from "../../../services/error_classifier.service";
import NoSearchResult from "../../../components/Common/NoSearchResult";

interface ModalErrorClassifierKeywordProps {
    isOpen: boolean;
    keywordId: number | null;
    onClose: () => void;
}

const ModalErrorClassifierKeyword: React.FC<ModalErrorClassifierKeywordProps> = ({isOpen, keywordId, onClose}) => {
    const isEditModal = keywordId !== null;
    const queryClient = useQueryClient();

    const [savedKeyword, setSavedKeyword] = useState<boolean>(false);
    const [removedKeyword, setRemovedKeyword] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const {
        data: keyword,
        isFetching: isFetchingKeyword,
    } = useQuery({
        queryKey: ["keyErrorClassifierKeyword", keywordId],
        queryFn: () => ErrorClassifierService.getKeywordById(keywordId || -1),
        enabled: isEditModal,
    });

    const selectGroups = (groups: IErrorClassifierGroup[]) =>
        groups.filter(group => !group.nostatus_group)
            .map((group) => ({label: group.name, value: group.id})).sort()

    const {
        data: groupsSelect,
        isLoading: isLoadingGroups,
    } = useQuery({
        queryKey: ["keyErrorClassifierGroups"],
        queryFn: () => ErrorClassifierService.getGroupsAll(),
        select: selectGroups
    });

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyErrorClassifierGroups"]})
            .then(() => onClose())
    }

    const {mutate: createKeyword, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IErrorClassifierKeywordForm) => ErrorClassifierService.createKeyword(data),
        onSuccess: onSuccess,
        mutationKey: ["keyCreateErrorClassifierKeyword"],
    });

    const {mutate: updateKeyword, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IErrorClassifierKeywordForm) => ErrorClassifierService.updateKeyword(keywordId!, data),
        onSuccess: onSuccess,
        mutationKey: ["keyUpdateErrorClassifierKeyword", keywordId],
    });

    const {mutate: removeKeyword, isPending: isPendingRemove} = useMutation({
        mutationFn: () => ErrorClassifierService.removeKeyword(keywordId!),
        onSuccess: onSuccess,
        mutationKey: ["keyRemoveErrorClassifierKeyword", keywordId],
    });

    const validationSchema: yup.Schema<IErrorClassifierKeywordForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("Keyword is required"),

        group_id: yup.number()
            .min(0, "Group is required")
            .required("Group is required"),
    });

    const initialValues: IErrorClassifierKeywordForm = {
        name: keyword?.name || "",
        group_id: keyword?.group_id || 0
    }

    const handleSubmitForm = (result: IErrorClassifierKeywordForm) => {
        setSavedKeyword(true);

        if (!isEditModal) createKeyword(result);
        else updateKeyword(result);
    };

    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const closeModal = () => {
        onClose();
        resetForm();
    };

    const handleChangeGroup = (select: any) => {
        setFieldValue("group_id", select.value)
    }

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
        } else {
            setSureToRemove(false)
            setRemovedKeyword(true)
            removeKeyword()
        }
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const selectedGroup = groupsSelect?.find((group) => group.value === values.group_id)

    const isPendingSave = isPendingCreate || isPendingUpdate || (!!queryClient.isFetching() && savedKeyword);
    const isPendingDelete = isPendingRemove || (!!queryClient.isFetching() && removedKeyword);

    const isFetching = isFetchingKeyword || (isLoadingGroups && !savedKeyword && !removedKeyword);

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                {isEditModal ? "Edit" : "Add"} Keyword
            </ModalHeader>
            {isFetching ? <div className="my-4"><NoSearchResult text="Loading..."/></div> : (
                <form onSubmit={handleSubmit} className="needs-validation">
                    <ModalBody>
                        <div className="mb-3">
                            <LabelInput
                                label="Keyword*"
                                placeholder="Keyword*"
                                name="name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                hasError={!!(errors.name && touched.name)}
                                errorText={errors.name}
                            />
                        </div>

                        <div>
                            <Label>Group: </Label>
                            <Select
                                isSearchable
                                name="group_id"
                                label="Group"
                                placeholder="Select a group"
                                options={groupsSelect}
                                onChange={handleChangeGroup}
                                value={selectedGroup}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <div className="w-100 d-flex justify-content-between align-items-center flex-row-reverse">
                            <Button type="submit" color="success" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>

                            {isEditModal && (
                                <Button color="danger" onClick={handleRemoveClick} disabled={isPendingDelete}>
                                    {isPendingDelete ? "Removing..." : sureToRemove ? "Confirm" : "Remove Keyword"}
                                </Button>
                            )}
                        </div>
                    </ModalFooter>
                </form>
            )}
        </Modal>
    )
}

export default ModalErrorClassifierKeyword